import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../../firebase/config';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc } from 'firebase/firestore';

// --- ICONOS SVG ---
const IconChevronLeft = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>;
const IconChevronRight = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>;

const AdminAcuerdos = () => {
    const navigate = useNavigate();
    const [acuerdos, setAcuerdos] = useState([]);
    
    // Listas para desplegables con sus IDs de Firestore
    const [listaCompanias, setListaCompanias] = useState([]);
    const [listaRiesgos, setListaRiesgos] = useState([]);
    
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(null);

    // Estados para los Filtros Dinámicos (Filtramos por NOMBRE para coincidir con la UI)
    const [filtroCompania, setFiltroCompania] = useState('');
    const [filtroRiesgo, setFiltroRiesgo] = useState('');

    // Estados para la Paginación
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // formData optimizado para almacenar la relación relacional (ID + Nombre)
    const [formData, setFormData] = useState({
        companiaId: '',
        compania: '',
        riesgoId: '',
        riesgo: '',
        subriesgo: '', // Opcional para herencia general
        comision: ''
    });

    const acuerdosRef = collection(db, 'public/data/acuerdos');

    // Cargar información inicial
    const fetchAllData = async () => {
        setLoading(true);
        try {
            const [acuerdosSnap, companiasSnap, riesgosSnap] = await Promise.all([
                getDocs(acuerdosRef),
                getDocs(collection(db, 'public/data/companias')),
                getDocs(collection(db, 'public/data/riesgos'))
            ]);

            const acuerdosData = acuerdosSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            acuerdosData.sort((a, b) => (a.compania || '').localeCompare(b.compania || ''));
            setAcuerdos(acuerdosData);

            // Guardamos ID y campos de Compañías
            const companiasData = companiasSnap.docs.map(d => ({
                id: d.id,
                ...d.data()
            })).sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
            setListaCompanias(companiasData);

            // Guardamos ID y campos de Riesgos (Inmuniza el React key r.id)
            const riesgosData = riesgosSnap.docs.map(d => ({ 
                id: d.id, 
                ...d.data() 
            })).sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
            setListaRiesgos(riesgosData);

        } catch (error) { 
            console.error("Error cargando acuerdos:", error); 
        } finally { 
            setLoading(false); 
        }
    };

    useEffect(() => { fetchAllData(); }, []);

    // Reiniciar paginación al cambiar los filtros
    useEffect(() => {
        setCurrentPage(1);
    }, [filtroCompania, filtroRiesgo]);

    // --- LOGICA DE FILTRADO DINÁMICO EN MEMORIA ---
    const acuerdosFiltrados = useMemo(() => {
        return acuerdos.filter(ac => {
            const matchCia = filtroCompania === '' || ac.compania === filtroCompania;
            const matchRiesgo = filtroRiesgo === '' || ac.riesgo === filtroRiesgo;
            return matchCia && matchRiesgo;
        });
    }, [acuerdos, filtroCompania, filtroRiesgo]);

    // --- LOGICA DE PAGINACIÓN ---
    const paginatedAcuerdos = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return acuerdosFiltrados.slice(startIndex, startIndex + itemsPerPage);
    }, [acuerdosFiltrados, currentPage]);

    const totalPages = Math.ceil(acuerdosFiltrados.length / itemsPerPage);

    // --- LOGICA MODAL ---
    const handleOpenModal = (item = null) => {
        if (item) {
            setFormData({
                companiaId: item.companiaId || '',
                compania: item.compania || '',
                riesgoId: item.riesgoId || '',
                riesgo: item.riesgo || '',
                subriesgo: item.subriesgo || '',
                comision: item.comision || ''
            });
            setIsEditing(item.id);
        } else {
            setFormData({ companiaId: '', compania: '', riesgoId: '', riesgo: '', subriesgo: '', comision: '' });
            setIsEditing(null);
        }
        setShowModal(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            // Guardamos tanto el ID como el Nombre (Esquema Híbrido Robusto)
            const payload = { 
                companiaId: formData.companiaId,
                compania: formData.compania,
                riesgoId: formData.riesgoId,
                riesgo: formData.riesgo,
                subriesgo: formData.subriesgo || '', 
                comision: Number(formData.comision) 
            };

            if (isEditing) {
                await updateDoc(doc(db, 'public/data/acuerdos', isEditing), payload);
            } else {
                await addDoc(acuerdosRef, payload);
            }
            
            setShowModal(false);
            
            // Recarga eficiente
            const snap = await getDocs(acuerdosRef);
            const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            data.sort((a, b) => (a.compania || '').localeCompare(b.compania || ''));
            setAcuerdos(data);
        } catch (error) { 
            console.error("Error al guardar acuerdo:", error);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("¿Desea eliminar este acuerdo comercial de comisión?")) {
            try {
                await deleteDoc(doc(db, 'public/data/acuerdos', id));
                setAcuerdos(prev => prev.filter(a => a.id !== id));
            } catch (error) {
                console.error("Error al eliminar acuerdo:", error);
            }
        }
    };

    // Manejador del select de Compañías
    const handleCompaniaChange = (e) => {
        const selectedId = e.target.value;
        const selectedObj = listaCompanias.find(c => c.id === selectedId);
        setFormData(prev => ({
            ...prev,
            companiaId: selectedId,
            compania: selectedObj ? selectedObj.nombre : ''
        }));
    };

    // Manejador del select de Riesgos (Limpia subriesgo si cambia el ramo)
    const handleRiesgoChange = (e) => {
        const selectedId = e.target.value;
        const selectedObj = listaRiesgos.find(r => r.id === selectedId);
        setFormData(prev => ({
            ...prev,
            riesgoId: selectedId,
            riesgo: selectedObj ? selectedObj.nombre : '',
            subriesgo: ''
        }));
    };

    const subriesgosDisponibles = formData.riesgoId 
        ? listaRiesgos.find(r => r.id === formData.riesgoId)?.subriesgos || [] 
        : [];

    return (
        <div className="p-8 bg-gray-50 min-h-screen font-sans text-gray-800 text-left">
            <div className="max-w-6xl mx-auto">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                    <div>
                        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Acuerdos Comerciales</h1>
                        <p className="text-gray-500 mt-1">Tabla de comisiones pactadas con Aseguradoras (Herencia General con excepciones).</p>
                    </div>
                    <div className="flex gap-3 shrink-0">
                        <button onClick={() => navigate('/AdminDashboard')} className="bg-white border border-gray-300 text-gray-700 px-5 py-2.5 rounded-xl font-bold shadow-sm hover:bg-gray-50 transition text-xs uppercase tracking-wider">Volver</button>
                        <button onClick={() => handleOpenModal()} className="bg-emerald-600 text-white px-5 py-2.5 rounded-xl font-bold shadow hover:bg-emerald-700 transition text-xs flex gap-2 items-center uppercase tracking-wider">+ Nuevo Acuerdo</button>
                    </div>
                </div>

                {/* --- BARRA DE FILTROS DINÁMICOS --- */}
                <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider block mb-1">Filtrar por Compañía</label>
                        <select 
                            value={filtroCompania} 
                            onChange={(e) => setFiltroCompania(e.target.value)} 
                            className="w-full border border-gray-200 rounded-xl p-2.5 text-xs font-bold bg-gray-50 text-slate-700 outline-none focus:ring-2 focus:ring-emerald-500 transition-all cursor-pointer"
                        >
                            <option value="">TODAS LAS COMPAÑÍAS</option>
                            {listaCompanias.map(c => <option key={c.id} value={c.nombre}>{c.nombre}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider block mb-1">Filtrar por Ramo / Riesgo</label>
                        <select 
                            value={filtroRiesgo} 
                            onChange={(e) => setFiltroRiesgo(e.target.value)} 
                            className="w-full border border-gray-200 rounded-xl p-2.5 text-xs font-bold bg-gray-50 text-slate-700 outline-none focus:ring-2 focus:ring-emerald-500 transition-all cursor-pointer"
                        >
                            <option value="">TODOS LOS RAMOS / RIESGOS</option>
                            {listaRiesgos.map(r => <option key={r.id} value={r.nombre}>{r.nombre}</option>)}
                        </select>
                    </div>
                </div>

                {/* --- TABLA DE ACUERDOS --- */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden flex flex-col">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-gray-50 text-gray-500 uppercase font-bold text-xs border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-4">Compañía</th>
                                <th className="px-6 py-4">Riesgo</th>
                                <th className="px-6 py-4">Producto / Subriesgo</th>
                                <th className="px-6 py-4 text-center">% Comisión</th>
                                <th className="px-6 py-4 text-center">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                <tr><td colSpan="5" className="text-center py-12 text-gray-400 font-bold">Cargando acuerdos...</td></tr>
                            ) : paginatedAcuerdos.length === 0 ? (
                                <tr><td colSpan="5" className="text-center py-12 text-gray-400 font-bold italic">No se encontraron acuerdos cargados que coincidan con los filtros.</td></tr>
                            ) : (
                                paginatedAcuerdos.map(ac => (
                                    <tr key={ac.id} className="hover:bg-emerald-50/20 transition">
                                        <td className="px-6 py-4 font-bold text-gray-900">{ac.compania}</td>
                                        <td className="px-6 py-4 text-gray-700 font-bold">{ac.riesgo}</td>
                                        <td className="px-6 py-4">
                                            {ac.subriesgo ? (
                                                <span className="text-blue-800 font-bold bg-blue-50 border border-blue-100 px-2.5 py-1 rounded-lg text-xs uppercase">
                                                    {ac.subriesgo}
                                                </span>
                                            ) : (
                                                <span className="text-gray-400 font-bold italic text-xs tracking-wide">
                                                    (GENERAL - Aplica a todo el Ramo)
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full font-black text-xs font-mono">{ac.comision}%</span>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <div className="flex justify-center gap-2">
                                                <button onClick={() => handleOpenModal(ac)} className="text-blue-500 hover:text-blue-700 p-1.5 hover:bg-blue-50 rounded-lg transition-colors" title="Editar"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536l12.232-12.232z"></path></svg></button>
                                                <button onClick={() => handleDelete(ac.id)} className="text-red-400 hover:text-red-600 p-1.5 hover:bg-red-50 rounded-lg transition-colors" title="Eliminar"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>

                    {/* --- CONTROLES DE PAGINACIÓN --- */}
                    {totalPages > 1 && (
                        <div className="h-14 bg-slate-50 border-t border-gray-100 flex justify-between items-center px-8 text-xs font-bold text-gray-500 shrink-0">
                            <span>Mostrando {paginatedAcuerdos.length} de {acuerdosFiltrados.length} acuerdos filtrados</span>
                            <div className="flex gap-2">
                                <button 
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                                    disabled={currentPage === 1} 
                                    className="px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-30 uppercase tracking-wider text-[10px] font-black transition-all shadow-sm flex items-center gap-1 cursor-pointer"
                                >
                                    Anterior
                                </button>
                                <span className="flex items-center px-4 font-black text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-lg">Página {currentPage} de {totalPages}</span>
                                <button 
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                                    disabled={currentPage === totalPages} 
                                    className="px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-30 uppercase tracking-wider text-[10px] font-black transition-all shadow-sm flex items-center gap-1 cursor-pointer"
                                >
                                    Siguiente
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* MODAL */}
            {showModal && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-100 text-left">
                        <div className="bg-emerald-600 px-6 py-4 flex justify-between items-center text-white">
                            <h3 className="font-bold text-lg">{isEditing ? 'Editar Acuerdo' : 'Nuevo Acuerdo'}</h3>
                            <button onClick={() => setShowModal(false)} className="hover:text-gray-200 font-bold font-mono">✕</button>
                        </div>
                        <form onSubmit={handleSave} className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-black text-gray-400 uppercase tracking-wider mb-1">Compañía *</label>
                                <select value={formData.companiaId} onChange={handleCompaniaChange} className="w-full border border-gray-300 rounded-xl p-2.5 outline-none bg-white text-xs font-bold text-slate-700 cursor-pointer" required>
                                    <option value="">Seleccione...</option>
                                    {listaCompanias.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-black text-gray-400 uppercase tracking-wider mb-1">Riesgo *</label>
                                <select value={formData.riesgoId} onChange={handleRiesgoChange} className="w-full border border-gray-300 rounded-xl p-2.5 outline-none bg-white text-xs font-bold text-slate-700 cursor-pointer" required>
                                    <option value="">Seleccione...</option>
                                    {listaRiesgos.map(r => <option key={r.id} value={r.id}>{r.nombre}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-black text-gray-400 uppercase tracking-wider mb-1">Subriesgo / Producto (Opcional)</label>
                                <select value={formData.subriesgo} onChange={(e) => setFormData({...formData, subriesgo: e.target.value})} className="w-full border border-gray-300 rounded-xl p-2.5 outline-none bg-white text-xs font-bold text-slate-700 disabled:bg-gray-100 cursor-pointer" disabled={!formData.riesgoId || subriesgosDisponibles.length === 0}>
                                    <option value="">(GENERAL - Aplica a todo el Ramo)</option>
                                    {subriesgosDisponibles.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                                <p className="text-[10px] text-gray-400 mt-1 italic font-bold">Deje esta opción si desea aplicar la misma comisión a todo el ramo.</p>
                            </div>
                            <div>
                                <label className="block text-xs font-black text-gray-400 uppercase tracking-wider mb-1">% Comisión Pactada *</label>
                                <div className="relative">
                                    <input type="number" step="0.01" value={formData.comision} onChange={(e) => setFormData({...formData, comision: e.target.value})} className="w-full border border-emerald-300 rounded-xl p-2.5 pr-8 outline-none focus:ring-2 focus:ring-emerald-500 font-bold text-lg font-mono" placeholder="0.00" required />
                                    <span className="absolute right-3 top-3.5 text-emerald-600 font-black text-sm">%</span>
                                </div>
                            </div>
                            <div className="pt-2 flex justify-end gap-2 border-t pt-4">
                                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-xl font-bold text-gray-500 text-xs hover:bg-gray-50 uppercase tracking-wider">Cancelar</button>
                                <button type="submit" className="px-6 py-2 rounded-xl bg-emerald-600 text-white font-bold hover:bg-emerald-700 text-xs shadow-md uppercase tracking-wider">Guardar</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminAcuerdos;